import dayjs from 'dayjs';
import { useEffect } from 'react';
import { calculatePeriodDateRange } from '../ui/period-selector';
import { useGlobalPeriodStore } from './period.store';

const lastCompletedMonthEnd = () => dayjs().subtract(1, 'month').endOf('month');

/**
 * Snaps the shared header range onto whole completed months while a months-only
 * tab is active (ST #137).
 *
 * The range is global, so arriving on the Financial tab from Operational carries
 * whatever was picked there -- a `1m` chip resolved day-wise, or a mid-month
 * From/To. Switching the picker to `month` alone would not fix that: the stored
 * range is only recomputed when the user touches a control, so the tab would
 * render a part-month range while displaying whole months.
 *
 * Leaving the tab deliberately does not snap back. A month-aligned range is
 * valid input for the day-based tabs, and reverting it would discard a choice
 * the user just made.
 */
export const useMonthsOnlyRangeSync = (enabled: boolean) => {
  const periodSelectorState = useGlobalPeriodStore(
    (state) => state.periodSelectorState
  );
  const changePeriodSelectorState = useGlobalPeriodStore(
    (state) => state.changePeriodSelectorState
  );

  useEffect(() => {
    if (!enabled) return;

    const { renderedDateRange: rendered, dateRange } = periodSelectorState;

    // A chip is selected: recompute it under months-only rules and keep the chip
    // highlighted, so 1M reads as "last completed month" rather than clearing.
    if (rendered?.period) {
      const { from, to } = calculatePeriodDateRange(rendered.period, true);

      if (
        dateRange.from?.isSame(from, 'day') &&
        dateRange.to?.isSame(to, 'day')
      )
        return;

      changePeriodSelectorState({
        dateRange: { from, to },
        renderedDateRange: { period: rendered.period },
      });
      return;
    }

    const from = rendered?.from ?? dateRange.from;
    const to = rendered?.to ?? dateRange.to;

    if (!from || !to) return;

    const maxTo = lastCompletedMonthEnd();

    let snappedFrom = from.startOf('month');
    let snappedTo = to.endOf('month');

    // The running month is never selectable here, so a range that reaches into
    // it is pulled back to the last completed month.
    if (snappedTo.isAfter(maxTo)) snappedTo = maxTo;
    if (snappedFrom.isAfter(snappedTo))
      snappedFrom = snappedTo.startOf('month');

    const alreadyAligned =
      snappedFrom.isSame(from, 'day') && snappedTo.isSame(to, 'day');

    if (alreadyAligned) return;

    changePeriodSelectorState({
      dateRange: { from: snappedFrom, to: snappedTo },
      renderedDateRange: { from: snappedFrom, to: snappedTo },
    });
  }, [enabled, periodSelectorState, changePeriodSelectorState]);
};
